import * as React from "react";
import { format, setMonth, setYear, getYear, getMonth } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// Generate year options (100 years back, 10 years forward)
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 111 }, (_, i) => currentYear - 100 + i);

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export function DatePicker({ 
  value, 
  onChange, 
  placeholder = "Seleccionar fecha",
  className,
  disabled = false,
  minYear,
  maxYear
}) {
  const [open, setOpen] = React.useState(false);
  const [viewDate, setViewDate] = React.useState(() => {
    if (value) {
      const parsed = value instanceof Date ? value : new Date(value);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    }
    return new Date();
  });
  
  // Parse the value - handle both Date objects and strings
  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }, [value]);

  // Update viewDate when value changes
  React.useEffect(() => {
    if (dateValue) {
      setViewDate(dateValue);
    }
  }, [dateValue]);

  const handleSelect = (day) => {
    const newDate = new Date(getYear(viewDate), getMonth(viewDate), day);
    const formatted = format(newDate, "yyyy-MM-dd");
    onChange(formatted);
    setOpen(false);
  };

  const handleMonthChange = (monthIndex) => {
    setViewDate(prev => setMonth(prev, monthIndex));
  };

  const handleYearChange = (year) => {
    setViewDate(prev => setYear(prev, parseInt(year)));
  };

  const handlePrevMonth = () => {
    setViewDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setViewDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = getYear(viewDate);
    const month = getMonth(viewDate);
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday
    
    // Adjust for Monday start (Spanish calendar)
    const adjustedStart = startingDay === 0 ? 6 : startingDay - 1;
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = adjustedStart - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, isCurrentMonth: false, isPast: true });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true });
    }
    
    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false, isFuture: true });
    }
    
    return days;
  };

  const days = generateCalendarDays();
  const selectedDay = dateValue && 
    getYear(dateValue) === getYear(viewDate) && 
    getMonth(dateValue) === getMonth(viewDate) 
      ? dateValue.getDate() 
      : null;

  const today = new Date();
  const isToday = (day) => 
    day === today.getDate() && 
    getMonth(viewDate) === today.getMonth() && 
    getYear(viewDate) === today.getFullYear();

  // Filter years based on min/max
  const filteredYears = yearOptions.filter(y => {
    if (minYear && y < minYear) return false;
    if (maxYear && y > maxYear) return false;
    return true;
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {dateValue ? (
            format(dateValue, "d 'de' MMMM 'de' yyyy", { locale: es })
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 z-[200]" align="start">
        <div className="p-3">
          {/* Month/Year selectors */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 hover:bg-muted rounded-md transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            <div className="flex items-center gap-2">
              {/* Month selector */}
              <select
                value={getMonth(viewDate)}
                onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                className="bg-background border border-input rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {monthNames.map((month, index) => (
                  <option key={month} value={index}>{month}</option>
                ))}
              </select>
              
              {/* Year selector */}
              <select
                value={getYear(viewDate)}
                onChange={(e) => handleYearChange(e.target.value)}
                className="bg-background border border-input rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {filteredYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-2 hover:bg-muted rounded-md transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'].map((day) => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((dayObj, index) => (
              <button
                key={index}
                type="button"
                onClick={() => dayObj.isCurrentMonth && handleSelect(dayObj.day)}
                disabled={!dayObj.isCurrentMonth}
                className={cn(
                  "h-9 w-9 rounded-md text-sm transition-colors",
                  dayObj.isCurrentMonth 
                    ? "hover:bg-muted cursor-pointer" 
                    : "text-muted-foreground/40 cursor-default",
                  selectedDay === dayObj.day && dayObj.isCurrentMonth && "bg-primary text-primary-foreground hover:bg-primary",
                  isToday(dayObj.day) && dayObj.isCurrentMonth && selectedDay !== dayObj.day && "bg-accent text-accent-foreground"
                )}
              >
                {dayObj.day}
              </button>
            ))}
          </div>
          
          {/* Quick actions */}
          <div className="flex justify-between mt-3 pt-3 border-t border-border">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                setViewDate(today);
                const formatted = format(today, "yyyy-MM-dd");
                onChange(formatted);
                setOpen(false);
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Hoy
            </button>
            {dateValue && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors"
              >
                Borrar
              </button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
